import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import { SharedService } from './shared.service';
import { DictionaryService } from './dictionary.service';
import { UserService } from 'app/shared/services/user.service';
import { ListService } from './list.service';

@Injectable()
export class GridService {
  public columnsDefaults: Array<any>;
  public reportColumns: Array<any>;
  public minColumnWidth: number;
  public defaultColumnWidth: number;
  public dataTotal: Array<any>;
  public measuresOptions: any;
  public rtbColumns: Array<any>;

  constructor(
    public sharedService: SharedService,
    public userService: UserService,
    public dictionaryService: DictionaryService
  ) {
    this.columnsDefaults = [];
    this.reportColumns = [];
    this.minColumnWidth = 120;
    this.defaultColumnWidth = 200;
    this.measuresOptions = {};
    this.rtbColumns = [];
    this.buildReportColumns();
    this.buildColumnsDefaults();
    this.buildRTBColumns();
  }

  parseRowAsObject(item: any) {
    const object: any = {};
    for (let i = 0; i < item.length; i++) {
      object[item[i].key] = item[i].originalValue;
    }
    return object;
  }

  getDataItem(data: Array<any>, item: any) {
    const itemId: number = parseInt(_.find(item, { key: '$$itemId' }).value);
    return _.find(data, function (o) {
      return o.$$itemId === itemId;
    });
  }

  getCellValue(cell: any, columnsSettings: Array<any>, isTooltip: boolean = false, isHeader: boolean = false) {
    const columnSettings: any = _.find(columnsSettings, { key: cell.key });
    let value: any = cell.value;
    let defaultValue: any;
    if (columnSettings) {
      if (this.sharedService.isEmpty(value)) {
        defaultValue = _.find(columnsSettings, { key: cell.key });
        if (defaultValue !== undefined) {
          value = defaultValue.defaultValue;
        }
      }
      if (!isTooltip) {
        if (
          columnSettings.parseFunction !== null &&
          columnSettings.parseFunction !== undefined &&
          columnSettings.parseFunction !== ''
        ) {
          value = !isHeader ? columnSettings.parseFunction(cell) : columnSettings.parseFunction(cell, true);
        }
      } else {
        if (
          columnSettings.parseTooltipFunction !== null &&
          columnSettings.parseTooltipFunction !== undefined &&
          columnSettings.parseTooltipFunction !== ''
        ) {
          value = columnSettings.parseTooltipFunction(cell);
        }
      }
      if (columnSettings.measurement !== undefined) {
        value =
          columnSettings.measurement.align === 'right'
            ? value + columnSettings.measurement.char
            : columnSettings.measurement.char + value;
      }
    }
    value = !this.sharedService.isEmpty(value) ? value.toString() : '';
    return value;
  }

  getActiveLabel(currRow) {
    return currRow.value === 'true' ? 'Active' : 'Inactive';
  }

  abbreviateNumber(num: number, key: string) {
    const defaultSettings: any = _.find(this.columnsDefaults, { key: key });
    let n: string = this.sharedService.abbreviateNumber(num);
    if (defaultSettings) {
      if (defaultSettings.measurement) {
        if (defaultSettings.measurement.align === 'right') {
          n += defaultSettings.measurement.char;
        } else {
          n = defaultSettings.measurement.char + n;
        }
      }
    }
    return n;
  }

  parseEnvironment(cell: any, isHeader: boolean = false) {
    let src = '';
    const width = '16px';
    const height = '16px';
    switch (cell.value.toString().toLowerCase()) {
      case 'desktop':
      case '1':
        src = 'desktop';
        break;
      case 'mobile web':
      case '2':
        src = 'mobile_web';
        break;
      case 'mobile app':
      case '3':
        src = 'mobile_app';
        break;
      case 'ctv':
      case '4':
        src = 'tv';
        break;
    }
    if (src === '') {
      return '';
    }
    return `<div><img src='./assets/img/icons/env/dark_grey/${src}.svg' width='${width}' height='${height}'></div>`;
  }

  parseEnvironmentTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    let tooltip = '';
    switch (cell.value.toString().toLowerCase()) {
      case 'desktop':
      case '1':
        tooltip = 'DESKTOP';
        break;
      case 'mobile web':
      case '2':
        tooltip = 'MOBILE_WEB';
        break;
      case 'mobile app':
      case '3':
        tooltip = 'MOBILE_APP';
        break;
      case 'ctv':
      case '4':
        tooltip = 'CTV';
        break;
    }
    if (tooltip === '') {
      return '';
    }
    return dictionaryService.get(tooltip);
  }

  parseMultipleEnvironments(cell: any, isHeader: boolean = false) {
    let imgs = `<div>`;
    const iconsFolder = `./assets/img/icons/env/dark_grey/`;
    const width = 16;
    const height = 16;
    if (cell.value) {
      if (cell.value.is_desktop) {
        imgs += `<img src='` + iconsFolder + `desktop.svg' width='` + width + `px' height='` + height + `px'></i>`;
      }
      if (cell.value.is_mobile_web) {
        imgs += `<img src='` + iconsFolder + `mobile_web.svg' width='` + width + `px' height='` + height + `px'></i>`;
      }
      if (cell.value.is_mobile_app) {
        imgs += `<img src='` + iconsFolder + `mobile_app.svg' width='` + width + `px' height='` + height + `px'></i>`;
      }
      if (cell.value.is_ctv) {
        imgs += `<img src='` + iconsFolder + `tv.svg' width='` + width + `px' height='` + height + `px'></i>`;
      }
    }
    imgs += `</div>`;
    return imgs;
  }

  parseMultipleEnvironmentsTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const tooltipLabels: Array<string> = [];
    let tooltip = '';
    if (cell.value) {
      if (cell.value.is_desktop) {
        tooltipLabels.push('DESKTOP');
      }
      if (cell.value.is_mobile_web) {
        tooltipLabels.push('MOBILE_WEB');
      }
      if (cell.value.is_mobile_app) {
        tooltipLabels.push('MOBILE_APP');
      }
      if (cell.value.is_ctv) {
        tooltipLabels.push('CTV');
      }
    }
    for (let i = 0; i < tooltipLabels.length; i++) {
      tooltip += dictionaryService.get(tooltipLabels[i]);
      if (i + 1 < tooltipLabels.length) {
        tooltip += ', ';
      }
    }
    return tooltip;
  }

  parseDay(cell: any, isHeader: boolean = false) {
    let day: string = cell.value;
    if (day.indexOf('-') === -1 && day.length > 0) {
      day = day.substr(0, 4) + '-' + day.substr(4, 2) + '-' + day.substr(6, 2);
    }
    return !isHeader ? moment(new Date(day)).format('YYYY-MM-DD') : '';
  }

  parseDayForReport(cell: any, isHeader: boolean = false) {
    const sharedService: SharedService = new SharedService(null, null, null, null);
    let selectedTimezone: any = localStorage.getItem('selectedTimezone');
    let offset: number = sharedService.offsetEST;
    let value: any = '';
    let day: string = cell.value;
    let year: string;
    let month: string;
    let date: string;
    if (!isHeader) {
      // Strip the value
      if (day.indexOf('-') === -1 && day.length > 0) {
        day = day.substr(0, 4) + '-' + day.substr(4, 2) + '-' + day.substr(6, 2);
      }
      // If we found the "selectedTimezone" object in the localStorage
      if (selectedTimezone) {
        // Find the offset
        selectedTimezone = JSON.parse(selectedTimezone);
        offset = moment.tz(new Date(), selectedTimezone.name)._offset / 60;
      }
      // Parse as a moment object, and set the offset (based on either on the selected timezone or the default EST offset)
      value = moment(day).utcOffset(offset);
      // Parse the value as a date
      value = value.toDate();
      // Extract the date, month and year
      year = value.getFullYear();
      month = sharedService.padNumber(value.getMonth() + 1);
      date = sharedService.padNumber(value.getDate());
      // Format the final value
      value = `${year}-${month}-${date}`;
    }
    return value;
  }

  parseDayTime(cell: any, isHeader: boolean = false) {
    return !isHeader ? moment(new Date(cell.value)).format('YYYY-MM-DD HH:mm:ss') : '';
  }

  parseTimestamp(cell: any) {
    const sharedService: SharedService = new SharedService(null, null, null, null);
    let selectedTimezone: any = localStorage.getItem('selectedTimezone');
    let offset: number = sharedService.offsetEST;
    const rawDate = new Date();
    if (selectedTimezone) {
      selectedTimezone = JSON.parse(selectedTimezone);
      offset = moment.tz(new Date(), selectedTimezone.name)._offset;
    }

    return cell.value && cell.value.length === 10
      ? moment(new Date((+cell.value + offset * 60 + rawDate.getTimezoneOffset() * 60) * 1000)).format(
          'YYYY-MM-DD HH:mm:ss'
        )
      : '-';
  }

  parseHour(cell: any, isHeader: boolean = false) {
    let hour: string = cell.value.toString();
    const timeOfDay: string = parseInt(cell.value) < 12 ? 'AM' : 'PM';
    if (parseInt(cell.value) >= 12) {
      hour = (parseInt(cell.value) - 12).toString();
    }
    if (parseInt(hour) === 0) {
      hour = '12';
    }
    if (parseInt(hour) < 10) {
      hour = '0' + hour;
    }
    return !isHeader ? hour + ':00 ' + timeOfDay : '';
  }

  parseHourString(val: string) {
    const hour: number = parseInt(val.match(/^(\d+)/)[1]);
    const timeOfDay: string = val.match(/\s(.*)$/)[1];
    if (timeOfDay === 'PM' && hour < 12) {
      return hour + 12;
    } else {
      return timeOfDay === 'AM' && hour === 12 ? hour - 12 : hour;
    }
  }

  parseList(cell: any, isHeader: boolean = false) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if (cell.value === 'B') {
      return dictionaryService.get('BLACK');
    } else if (cell.value === 'W') {
      return dictionaryService.get('WHITE');
    } else {
      return '';
    }
  }

  parseListTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if (cell.value === 'B') {
      return dictionaryService.get('BLACK');
    } else if (cell.value === 'W') {
      return dictionaryService.get('WHITE');
    } else {
      return '';
    }
  }

  parseBoolean(cell: any, isHeader: boolean = false) {
    const icon: string = cell.value.toString() === 'true' || cell.value.toString() === '1' ? 'check' : 'times';
    return !isHeader ? "<i class='fa fa-" + icon + "'></i>" : '';
  }

  parseBooleanTooltip(cell: any = {}) {
    let tooltip: string;
    const dictionaryService: DictionaryService = new DictionaryService();
    tooltip =
      cell.value !== undefined && cell.value !== null
        ? cell.value.toString() === 'true' || cell.value.toString() === '1'
          ? 'YES'
          : 'NO'
        : '';
    return dictionaryService.get(tooltip);
  }

  parseActions(cell: any, isHeader: boolean = false) {
    let str = '';
    if (cell.value) {
      for (let i = 0; i < cell.value.length; i++) {
        str += cell.value[i].type;
        if (i + 1 < cell.value.length) {
          str += ',';
        }
      }
    }
    return str;
  }

  parseActive(cell: any, isHeader: boolean = false) {
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const src: string = state ? 'active' : 'inactive';
    return `<img src="./assets/img/icons/` + src + `.svg" width="15" height="13">`;
  }

  parseRunning(cell: any) {
    const src: string = cell.value === 'Running' ? 'cellGreen' : '';
    return `<i class="fa fa-circle ${src}"></i>`;
  }

  parseRunningTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip: string = state ? 'IS_RUNNING' : 'NOT_RUNNING';
    return dictionaryService.get(tooltip);
  }

  parseLiveTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip: string = state ? 'LIVE' : 'NOT_LIVE';
    return dictionaryService.get(tooltip);
  }

  parseActiveTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip: string = state ? 'ACTIVE' : 'INACTIVE';
    return dictionaryService.get(tooltip);
  }

  parseActiveSupplyTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip: string = state ? 'CLICK_TO_DEACTIVATE_SUPPLY' : 'CLICK_TO_ACTIVATE_SUPPLY';
    return dictionaryService.get(tooltip);
  }

  parseActiveDemandTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip: string = state ? 'CLICK_TO_DEACTIVATE_DEMAND' : 'CLICK_TO_ACTIVATE_DEMAND';
    return dictionaryService.get(tooltip);
  }

  parsePassedPaymentTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const state: boolean = !_.isNull(cell.value)
      ? cell.value.toString() === 'true' || cell.value.toString() === '1'
      : false;
    const tooltip = state ? 'PASSED' : 'NOT_PASSED';
    return dictionaryService.get(tooltip);
  }

  parseSelectedTooltip(cell: any) {
    let tooltip: string;
    const dictionaryService: DictionaryService = new DictionaryService();
    tooltip = cell.value ? 'SELECTED' : 'NOT_SELECTED';
    return dictionaryService.get(tooltip);
  }

  parseNumeric(cell: any, isHeader: boolean = false) {
    let num: any = cell.value;
    if (num === undefined || num === null || num === '' || isNaN(Number(num))) {
      num = 0;
    }
    num = parseFloat(num);
    return Number(num).toLocaleString();
  }

  parseNumericPadd(cell: any, isHeader: boolean = false) {
    let num: any = cell.value;
    if (num === undefined || num === null || num === '' || isNaN(Number(num))) {
      num = 0;
    }
    num = parseFloat(num).toFixed(2);
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  parseSupplyMethod(cell: any, isHeader: boolean = false) {
    let label = '';
    const dictionaryService: DictionaryService = new DictionaryService();
    if (isNaN(parseInt(cell.value))) {
      return cell.value;
    }
    switch (cell.value.toString()) {
      case '0':
      case 'Regular':
        label = 'REGULAR';
        break;
      case '1':
      case 'HB':
        label = 'HB_';
        break;
    }
    return label === '' ? '' : dictionaryService.get(label);
  }

  parseSupplyType(cell: any, isHeader: boolean = false) {
    let label = '';
    const dictionaryService: DictionaryService = new DictionaryService();
    if (isNaN(parseInt(cell.value))) {
      return cell.value;
    }
    switch (cell.value.toString()) {
      case '0':
      case 'HTML':
        label = 'HTML_PLAYER';
        break;
      case '1':
      case 'VAST':
        label = 'VAST';
        break;
      case '2':
      case 'CedatoX':
        label = 'CEDATOX';
        break;
      case '3':
      case 'VastCTV':
        label = 'VASTCTV';
        break;
    }
    return label === '' ? '' : dictionaryService.get(label);
  }

  parseSupplySubType(cell: any, isHeader: boolean = false) {
    let label = '';
    const dictionaryService: DictionaryService = new DictionaryService();
    if (isNaN(parseInt(cell.value))) {
      return cell.value;
    }
    switch (cell.value.toString()) {
      case '0':
        label = 'SUPPLY_SUB_TYPE_REGULAR';
        break;
      case '1':
        label = 'SUPPLY_SUB_TYPE_CONTENT_UNIT';
        break;
      case '2':
        label = 'SUPPLY_SUB_TYPE_EMPTY';
        break;
    }
    return label === '' ? '' : dictionaryService.get(label);
  }
  parseDemandType(cell: any, isHeader: boolean = false) {
    let label = '';
    const dictionaryService: DictionaryService = new DictionaryService();
    if (isNaN(parseInt(cell.value))) {
      return cell.value;
    }
    switch (cell.value.toString()) {
      case '0':
      case 'VAST':
        label = 'VAST';
        break;
      case '1':
      case 'CedatoX':
        label = 'CEDATOX';
        break;
      case '2':
      case 'SpotX Direct':
      case 'SpotX':
        label = 'SPOTX_DIRECT';
        break;
      case '3':
      case 'RTB':
        label = 'RTB';
        break;
      case '4':
      case 'PREBID_VAST_TYPE':
        label = 'PREBID_VAST_TYPE';
        break;
    }
    return label === '' ? '' : dictionaryService.get(label);
  }

  parseFileType(cell: any, isHeader: boolean = false) {
    let label = '';
    const dictionaryService: DictionaryService = new DictionaryService();
    switch (cell.value.toString()) {
      case '1':
        label = 'IMAGE';
        break;
      case '2':
        label = 'VIDEO';
        break;
      case '3':
        label = 'APPLICATION';
        break;
      case '4':
        label = 'TEXT';
        break;
    }
    return label === '' ? '' : dictionaryService.get(label);
  }

  parseListType(cell: any, isHeader: boolean = false) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const listService: ListService = new ListService();
    const listType: any = _.find(listService.listTypes, { id: cell.value });
    if (listType) {
      return dictionaryService.get(listType.name);
    }
    return '';
  }

  parseNetworkType(cell: any, isHeader: boolean = false) {
    const dictionaryService: DictionaryService = new DictionaryService();
    switch (cell.value) {
      case 'regular':
        return dictionaryService.get('NETWORK');
      case 'publisher':
        return dictionaryService.get('PUBLISHER');
      case 'agg':
        return dictionaryService.get('AGGREGATOR');
    }
    return '';
  }

  parseListConditionType(cell: any, isHeader: boolean = false) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if (cell.value === '1') {
      return dictionaryService.get('WHITE');
    } else if (cell.value === '2') {
      return dictionaryService.get('BLACK');
    }
    return '';
  }

  parseSource(cell: any, isHeader: boolean) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if ((cell.value === '' || cell.value === undefined || cell.value === null) && !isHeader) {
      return `<span class='badText'>` + dictionaryService.get('NOT_PROVIDED_BY_SOURCE') + `</span>`;
    }
    return cell.value;
  }

  parseSourceTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if (cell.value === '' || cell.value === undefined || cell.value === null) {
      return dictionaryService.get('NOT_PROVIDED_BY_SOURCE_TOOLTIP');
    }
    return cell.value;
  }

  parsePartner(cell: any, isHeader: boolean) {
    const dictionaryService: DictionaryService = new DictionaryService();
    if ((cell.value === '' || cell.value === undefined || cell.value === null) && !isHeader) {
      return dictionaryService.get('NOT_DEFINED');
    }
    return cell.value;
  }

  parseBroadcastColor(cell: any, isHeader: boolean = false) {
    const colors: Array<string> = ['RED', 'GREEN', 'YELLOW'];
    const colorId: number = parseInt(cell.value);
    const dictionaryService: DictionaryService = new DictionaryService();
    return colorId >= 0 && colorId <= colors.length ? dictionaryService.get(colors[colorId]) : '';
  }

  parseBroadcastStatus(cell: any, isHeader: boolean = false) {
    const dictionaryService: DictionaryService = new DictionaryService();
    const value: string = cell.value.toString();
    let label = '';
    if (value === '0') {
      label = 'DRAFT';
    } else if (value === '1') {
      label = 'PUBLISHED';
    } else if (value === '2') {
      label = 'DISMISSED';
    }
    return label !== '' ? dictionaryService.get(label) : label;
  }

  parsePublisherHB(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    return cell.value ? dictionaryService.get('HB') : dictionaryService.get('REGULAR');
  }

  parsePublisherHBTooltip(cell: any) {
    const dictionaryService: DictionaryService = new DictionaryService();
    let tooltip: string;
    tooltip =
      cell.value !== undefined && cell.value !== null
        ? cell.value.toString() === 'true' || cell.value.toString() === '1'
          ? 'YES'
          : 'NO'
        : '';

    return cell.disabled ? dictionaryService.get('DISABLED_BECAUSE_OF_PUBLISHER_IO') : dictionaryService.get(tooltip);
  }

  getColumnDefaults(column: any) {
    return column.key && column.key.length > 2
      ? _.find(this.columnsDefaults, { key: column.key })
      : _.find(this.columnsDefaults, { label: column.label });
  }

  getReportColumnTooltip(cell: any, columnsSettings: Array<any>) {
    let tooltip: string = cell.value;
    const dictionaryService: DictionaryService = new DictionaryService();
    const reportColumn: any = _.find(this.reportColumns, { actualKey: cell.key });
    const hasDemand: boolean = _.find(columnsSettings, { key: 'demand_id' });
    if (reportColumn) {
      if (reportColumn.tooltipKey) {
        tooltip = dictionaryService.get(reportColumn.tooltipKey);
        if (hasDemand && reportColumn.tooltipKeyDemand) {
          tooltip = dictionaryService.get(reportColumn.tooltipKeyDemand);
        }
      }
    }
    return tooltip;
  }

  getReportKeyByActualKey(key: string) {
    return _.find(this.reportColumns, { actualKey: key }).reportKey;
  }

  getReportColumnSettings(key: string) {
    const reportColumn: any = _.find(this.reportColumns, { reportKey: key });
    let columnSettings: any;
    if (reportColumn === undefined) {
      return {};
    }
    columnSettings = _.find(this.columnsDefaults, { key: reportColumn.actualKey });
    if (columnSettings === undefined) {
      return {};
    }
    for (const property in reportColumn) {
      if (property !== 'actualKey' && property !== 'reportKey') {
        columnSettings[property] = reportColumn[property];
      }
    }
    return columnSettings;
  }

  getNonEmptyFilters(filters: any) {
    const nonEmptyFilters: any = {};
    let addCurrentFilter: boolean;
    for (const property in filters) {
      addCurrentFilter = false;
      if (filters[property] instanceof Object && !(filters[property] instanceof Array)) {
        addCurrentFilter = filters[property].min !== null && filters[property].max !== null;
      } else {
        addCurrentFilter = !this.sharedService.isEmpty(filters[property]);
      }
      if (addCurrentFilter) {
        nonEmptyFilters[property] = filters[property];
      }
    }
    return nonEmptyFilters;
  }

  toFixed(n: number) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  aggregate(data: Array<any>, columnsSettings: Array<any>, property: string) {
    const columnSettings: any = _.find(columnsSettings, { key: property });
    if (columnSettings) {
      if (columnSettings.aggregateFunction) {
        return columnSettings.aggregateFunction(data, this);
      }
    }
    switch (property) {
      case 'fillrate':
        return this.aggregateFillrate(data);
      case 'rpm':
      case 'RPM':
        return this.aggregateRPM(data);
      case 'impression_rpm':
        return this.aggregateImpressionRPM(data);
      case 'demand_fr':
        return this.aggregateDemandFillrate(data);
      case 'startFill':
        return this.aggregateStartFill(data);
      case 'adStartRPM':
        return this.aggregateAdStartRPM(data);
      case 'adStartsPercent':
        return this.aggregateAdStartsPercent(data);
      case 'prebid_cpm':
      case 'rtb_bid_cpm':
        return this.aggregateBidCPM(data);
      case 'ad_impression_duration':
        return this.aggregateAdImpressionDuration(data);
      case 'ad_loaded_duration':
        return this.aggregateAdLoadedDuration(data);
      case 'vcr':
        return this.aggregateVCR(data);
      case 'viewability':
        return this.aggregateVWY(data);
      case 'viewability_sampled':
        return this.aggregateVWS(data);
      default:
        return this.aggregateSum(data, property);
    }
  }

  aggregateSum(data: Array<any>, property: string) {
    let aggreagted = 0;
    let n: number;
    data.forEach((item: any) => {
      n = item ? parseFloat(item[property]) : 0;
      if (!isNaN(n)) {
        aggreagted += n;
      }
    });
    return aggreagted;
  }

  aggregateAvg(data: Array<any>) {
    let aggreagted = 0;
    let n: number;
    data.forEach((item: any) => {
      n = item ? parseFloat(item[item]) : 0;
      if (!isNaN(n)) {
        aggreagted += n;
      }
    });
    return aggreagted / data.length;
  }

  aggregateAdLoadedDuration(data: Array<any>) {
    let aggregated: number;
    let adloaddur = 0;
    let adloaded = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.ad_loaded_duration);
        b = parseFloat(item.ad_loaded);
        if (!isNaN(a) && !isNaN(b)) {
          adloaddur += a;
          adloaded += b;
        }
      }
    });
    aggregated = adloaded > 0 ? adloaddur / adloaded : 0;
    return this.toFixed(aggregated);
  }

  aggregateAdImpressionDuration(data: Array<any>) {
    let aggregated: number;
    let adimprdur = 0;
    let adimpr = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.ad_impression_duration);
        b = parseFloat(item.ad_impression);
        if (!isNaN(a) && !isNaN(b)) {
          adimprdur += a;
          adimpr += b;
        }
      }
    });
    aggregated = adimpr > 0 ? adimprdur / adimpr : 0;
    return this.toFixed(aggregated);
  }

  aggregateBidCPM(data: Array<any>) {
    let aggregated: number;
    let bidcpm = 0;
    let bidresp = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        const item_bid_cpm = item.prebid_cpm ? item.prebid_cpm : item.rtb_bid_cpm;
        a = parseFloat(item_bid_cpm);
        const item_bid_responses = item.prebid_responses ? item.prebid_responses : item.rtb_bid_responses;
        b = parseFloat(item_bid_responses);
        if (!isNaN(a) && !isNaN(b)) {
          bidcpm += a;
          bidresp += b;
        }
      }
    });
    aggregated = bidresp > 0 ? bidcpm / bidresp : 0;
    return this.toFixed(aggregated);
  }

  aggregateFillrate(data: Array<any>) {
    let aggregated: number;
    let impressions = 0;
    let opportunities = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = item.impressions ? parseFloat(item.impressions) : item.ad_impression ? parseFloat(item.ad_impression) : 0;
        b = item.opportunities
          ? parseFloat(item.opportunities)
          : item.supply_opportunities
          ? parseFloat(item.supply_opportunities)
          : item.demand_opportunities
          ? parseFloat(item.demand_opportunities)
          : 0;
        if (!isNaN(a) && !isNaN(b)) {
          impressions += a;
          opportunities += b;
        }
      }
    });
    aggregated = opportunities > 0 ? (impressions / opportunities) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateDemandFillrate(data: Array<any>) {
    let aggregated: number;
    let impressions = 0;
    let ad_requests = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = item.impressions ? parseFloat(item.impressions) : item.ad_impression ? parseFloat(item.ad_impression) : 0;
        b = parseFloat(item.ad_requests);
        if (!isNaN(a) && !isNaN(b)) {
          impressions += a;
          ad_requests += b;
        }
      }
    });
    aggregated = ad_requests > 0 ? (impressions / ad_requests) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateRPM(data: Array<any>) {
    let aggregated: number;
    let revenue = 0;
    let opportunities = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.revenue);
        b = item.opportunities
          ? parseFloat(item.opportunities)
          : item.supply_opportunities
          ? parseFloat(item.supply_opportunities)
          : item.demand_opportunities
          ? parseFloat(item.demand_opportunities)
          : 0;
        if (!isNaN(a) && !isNaN(b)) {
          revenue += a;
          opportunities += b;
        }
      }
    });
    aggregated = opportunities > 0 ? (revenue / opportunities) * 1000 : 0;
    return this.toFixed(aggregated);
  }

  aggregateImpressionRPM(data: Array<any>) {
    let aggregated: number;
    let revenue = 0;
    let impressions = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.revenue);
        b = item.impressions ? parseFloat(item.impressions) : item.ad_impression ? parseFloat(item.ad_impression) : 0;
        if (!isNaN(a) && !isNaN(b)) {
          revenue += a;
          impressions += b;
        }
      }
    });
    aggregated = impressions > 0 ? (revenue / impressions) * 1000 : 0;
    return this.toFixed(aggregated);
  }

  aggregateStartFill(data: Array<any>) {
    let aggregated: number;
    let adStarts = 0;
    let startReqs = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.adStarts);
        b = parseFloat(item.startReqs);
        if (!isNaN(a) && !isNaN(b)) {
          adStarts += a;
          startReqs += b;
        }
      }
    });
    aggregated = startReqs > 0 ? (adStarts / startReqs) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateAdStartRPM(data: Array<any>) {
    let aggregated: number;
    let revenue = 0;
    let adStarts = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.revenue);
        b = parseFloat(item.adStarts);
        if (!isNaN(a) && !isNaN(b)) {
          revenue += a;
          adStarts += b;
        }
      }
    });
    aggregated = adStarts > 0 ? (revenue / adStarts) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateAdStartsPercent(data: Array<any>) {
    let aggregated: number;
    let impressions = 0;
    let adStarts = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = parseFloat(item.revenue);
        b = parseFloat(item.adStarts);
        if (!isNaN(a) && !isNaN(b)) {
          impressions += a;
          adStarts += b;
        }
      }
    });
    aggregated = adStarts > 0 ? (impressions / adStarts) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateVCR(data: Array<any>) {
    let aggregated: number;
    let completed = 0;
    let impression = 0;
    let a: number;
    let b: number;
    data.forEach((item: any) => {
      if (item) {
        a = +item.ad_q4_played;
        b = +item.ad_impression;
        if (!isNaN(a) && !isNaN(b)) {
          completed += a;
          impression += b;
        }
      }
    });
    aggregated = impression > 0 ? (completed / impression) * 100 : 0;
    return this.toFixed(aggregated);
  }

  aggregateVWY(data: Array<any>) {
    let aggregated: number;
    let viewability = 0;
    let a = 0;
    data.forEach((item: any) => {
      if (item && !isNaN(+item.vwy)) {
        viewability += +item.vwy;
        a++;
      }
    });
    aggregated = a > 0 ? viewability / a : 0;
    return this.toFixed(aggregated);
  }

  aggregateVWS(data: Array<any>) {
    let aggregated: number;
    let viewability_s = 0;
    let a = 0;
    data.forEach((item: any) => {
      if (item && !isNaN(+item.vws)) {
        viewability_s += +item.vws;
        a++;
      }
    });
    aggregated = a > 0 ? viewability_s / a : 0;
    return this.toFixed(aggregated);
  }

  buildReportColumns(ver: number = 1) {
    this.reportColumns = [
      { reportKey: 'sp', actualKey: 'supply_partner' },
      { reportKey: 'dp', actualKey: 'demand_partner' },
      { reportKey: 'si', actualKey: 'supply_id' },
      { reportKey: 'sn', actualKey: 'supply_name' },
      { reportKey: 'sm', actualKey: 'supply_method' },
      { reportKey: 'st', actualKey: 'supply_type' },
      { reportKey: 'di', actualKey: 'demand_id' },
      { reportKey: 'dn', actualKey: 'demand_name' },
      { reportKey: 'partner_demand_id', actualKey: 'partner_demand_id' },
      { reportKey: 'partner_demand_name', actualKey: 'partner_demand_name' },
      { reportKey: 'cs', actualKey: 'capping_pause' },
      { reportKey: 'dt', actualKey: 'demand_type' },
      { reportKey: 'du', actualKey: 'demand_url' },
      { reportKey: 'sb', actualKey: 'sub_id' },
      { reportKey: 's2', actualKey: 'sub_id2' },
      { reportKey: 's3', actualKey: 'sub_id3' },
      { reportKey: 's4', actualKey: 'sub_id4' },
      { reportKey: 'ab', actualKey: 'app_bundle' },
      { reportKey: 'an', actualKey: 'app_name' },
      { reportKey: 'co', actualKey: 'country' },
      { reportKey: 'dy', actualKey: 'day', filterType: 'DROPDOWN', parseFunction: this.parseDayForReport },
      { reportKey: 'hr', actualKey: 'hour', filterType: 'DROPDOWN' },
      { reportKey: 'dm', actualKey: 'domain' },
      { reportKey: 'dd', actualKey: 'detected_domain' },
      { reportKey: 'en', actualKey: 'environment', label: 'ENVIRONMENT_DETECTED' },
      { reportKey: 'ps', actualKey: 'player_size' },
      { reportKey: 'sd', actualKey: 'detected_player_size' },
      { reportKey: 'pv', actualKey: 'player_version', filterType: 'DROPDOWN' },
      { reportKey: 'ui', actualKey: 'ua_id' },
      { reportKey: 'vn', actualKey: 'vendor' },
      { reportKey: 'ci', actualKey: 'content_id' },
      { reportKey: 'cn', actualKey: 'content_name' },
      { reportKey: 'ma', actualKey: 'dma' },
      { reportKey: 'se', actualKey: 'state' },
      { reportKey: 'am', actualKey: 'acc_manager' },
      { reportKey: 'ni', actualKey: 'network_id' },
      { reportKey: 'nt', actualKey: 'network_type' },
      { reportKey: 'nn', actualKey: 'network_name' },
      { reportKey: 'up', actualKey: 'utm_campaign' },
      { reportKey: 'um', actualKey: 'utm_medium' },
      { reportKey: 'us', actualKey: 'utm_source' },
      { reportKey: 'ut', actualKey: 'utm_term' },
      { reportKey: 'uc', actualKey: 'utm_content' },
    ];

    this.reportColumns = _.union(this.reportColumns, this.buildMeasureColumns(ver));
  }

  renameKeys(obj, keys) {
    _.forOwn(keys, function(newKey, key) {
      obj[newKey] = obj[key];
      delete obj[key];
    });
  }

  mergePartnerReports(partnerReport, initReport) {
    this.renameKeys(partnerReport, {
      'si': 'partner_id',
      'di': 'partner_demand_id',
      'dn': 'partner_demand_name',
      'du': 'partner_demand_url',
      'clk': 'ad_clicked',
      'adi': 'ad_impression',
      'pq4': 'ad_q4_played',
      'adr': 'ad_requests',
      'cs': 'capping_pause',
      'ccl': 'content_clicks',
      'imr': 'impression_rpm',
      'rev': 'revenue',
      'srq': 'start_requests',
      'vwy': 'viewability',
      'vws': 'viewability_sampled',
    });
    return Object.assign({}, initReport, partnerReport);
  }

  buildMeasureColumns(ver: number = 1) {
    let measureColumns: Array<any> = [];
    switch (ver) {
      case 2:
        measureColumns = [
          {
            reportKey: 'cst',
            actualKey: 'cost',
            tooltipKey: this.userService.isCedatoPublisher() ? 'REPORT_TOOLTIP_R' : 'REPORT_TOOLTIP_C',
            aggregated: true,
          },
          { reportKey: 'dcs', actualKey: 'demand_cost', tooltipKey: 'REPORT_TOOLTIP_DCS', aggregated: true },
          {
            reportKey: 'adi',
            actualKey: 'ad_impression',
            tooltipKey: 'REPORT_TOOLTIP_I',
            tooltipKeyDemand: 'REPORT_TOOLTIP_I_DEMAND',
            aggregated: true,
          },
          {
            reportKey: 'rev',
            actualKey: 'revenue',
            tooltipKey: 'REPORT_TOOLTIP_R',
            tooltipKeyDemand: 'REPORT_TOOLTIP_R_DEMAND',
            aggregated: true,
          },
          { reportKey: 'sop', actualKey: 'supply_opportunities', tooltipKey: 'REPORT_TOOLTIP_SOP', aggregated: true },
          { reportKey: 'dop', actualKey: 'demand_opportunities', tooltipKey: 'REPORT_TOOLTIP_DOP', aggregated: true },
          { reportKey: 'sld', actualKey: 'supply_loaded', tooltipKey: 'REPORT_TOOLTIP_SLD', aggregated: true },
          { reportKey: 'pq1', actualKey: 'ad_q1_played', tooltipKey: 'REPORT_TOOLTIP_PQ1', aggregated: true },
          { reportKey: 'pq2', actualKey: 'ad_q2_played', tooltipKey: 'REPORT_TOOLTIP_PQ2', aggregated: true },
          { reportKey: 'pq3', actualKey: 'ad_q3_played', tooltipKey: 'REPORT_TOOLTIP_PQ3', aggregated: true },
          { reportKey: 'pq4', actualKey: 'ad_q4_played', tooltipKey: 'REPORT_TOOLTIP_PQ4', aggregated: true },
          { reportKey: 'vcr', actualKey: 'vcr', tooltipKey: 'REPORT_TOOLTIP_VCR', aggregated: true },
          { reportKey: 'clk', actualKey: 'ad_clicked', tooltipKey: 'REPORT_TOOLTIP_CLK', aggregated: true },
          { reportKey: 'ccl', actualKey: 'content_clicks', tooltipKey: 'REPORT_TOOLTIP_CCL', aggregated: true },
          { reportKey: 'cvw', actualKey: 'content_viewed', tooltipKey: 'REPORT_TOOLTIP_CVW', aggregated: true },
          { reportKey: 'ccd', actualKey: 'content_completed', tooltipKey: 'REPORT_TOOLTIP_CCD', aggregated: true },
          { reportKey: 'adl', actualKey: 'ad_loaded', tooltipKey: 'REPORT_TOOLTIP_ADL', aggregated: true },
          { reportKey: 'ast', actualKey: 'ad_start_avg_time', tooltipKey: 'REPORT_TOOLTIP_AST', aggregated: true },
          { reportKey: 'alt', actualKey: 'ad_load_avg_time', tooltipKey: 'REPORT_TOOLTIP_ALT', aggregated: true },
          { reportKey: 'adr', actualKey: 'ad_requests', tooltipKey: 'REPORT_TOOLTIP_A', aggregated: true },
          { reportKey: 'srq', actualKey: 'start_requests', tooltipKey: 'REPORT_TOOLTIP_SRQ', aggregated: true },
          { reportKey: 'dfr', actualKey: 'demand_fr', tooltipKey: 'REPORT_TOOLTIP_D', aggregated: true },
          { reportKey: 'fir', actualKey: 'fillrate', tooltipKey: 'REPORT_TOOLTIP_F', aggregated: true },
          { reportKey: 'rpm', actualKey: 'rpm', tooltipKey: 'REPORT_TOOLTIP_M', aggregated: true },
          { reportKey: 'imr', actualKey: 'impression_rpm', tooltipKey: 'REPORT_TOOLTIP_P', aggregated: true },
          { reportKey: 'prv', actualKey: 'previewed', tooltipKey: 'REPORT_TOOLTIP_PRV', aggregated: true },
          { reportKey: 'tpr', actualKey: 'previews', tooltipKey: 'REPORT_TOOLTIP_TPR', aggregated: true },
          { reportKey: 'vwy', actualKey: 'viewability', tooltipKey: 'REPORT_TOOLTIP_VWY', aggregated: true },
          { reportKey: 'vws', actualKey: 'viewability_sampled', tooltipKey: 'REPORT_TOOLTIP_VWS', aggregated: true },
          { reportKey: 'brq', actualKey: 'rtb_bid_requests', aggregated: true },
          { reportKey: 'brs', actualKey: 'rtb_bid_responses', aggregated: true },
          { reportKey: 'nbr', actualKey: 'rtb_no_bid_responses', aggregated: true },
          { reportKey: 'bcp', actualKey: 'rtb_bid_cpm', aggregated: true },
          { reportKey: 'pbq', actualKey: 'prebid_requests', aggregated: true },
          { reportKey: 'pbs', actualKey: 'prebid_responses', aggregated: true },
          { reportKey: 'pbc', actualKey: 'prebid_cpm', aggregated: true },
        ];
        break;

      default:
        measureColumns = [
          { reportKey: 'c', actualKey: 'cost', tooltipKey: 'REPORT_TOOLTIP_C', aggregated: true },
          { reportKey: 'a', actualKey: 'ad_requests', tooltipKey: 'REPORT_TOOLTIP_A', aggregated: true },
          {
            reportKey: 'i',
            actualKey: 'impressions',
            tooltipKey: 'REPORT_TOOLTIP_I',
            tooltipKeyDemand: 'REPORT_TOOLTIP_I_DEMAND',
            aggregated: true,
          },
          {
            reportKey: 'r',
            actualKey: 'revenue',
            tooltipKey: 'REPORT_TOOLTIP_R',
            tooltipKeyDemand: 'REPORT_TOOLTIP_R_DEMAND',
            aggregated: true,
          },
          {
            reportKey: 'p',
            actualKey: 'impression_rpm',
            tooltipKey: 'REPORT_TOOLTIP_P',
            tooltipKeyDemand: 'REPORT_TOOLTIP_P_DEMAND',
            aggregated: true,
          },
          { reportKey: 'o', actualKey: 'opportunities', tooltipKey: 'REPORT_TOOLTIP_O', aggregated: true },
          { reportKey: 'm', actualKey: 'rpm', tooltipKey: 'REPORT_TOOLTIP_M', aggregated: true },
          { reportKey: 'f', actualKey: 'fillrate', tooltipKey: 'REPORT_TOOLTIP_F', aggregated: true },
          { reportKey: 'd', actualKey: 'demand_fr', tooltipKey: 'REPORT_TOOLTIP_D', aggregated: true },
          { reportKey: 'b', actualKey: 'rtb_bid_requests', aggregated: true },
          { reportKey: 's', actualKey: 'rtb_bid_responses', aggregated: true },
          { reportKey: 'n', actualKey: 'rtb_no_bid_responses', aggregated: true },
          { reportKey: 'q', actualKey: 'rtb_bid_cpm', aggregated: true },
        ];
    }

    measureColumns = _.map(measureColumns, (one: any) => {
      if (this.measuresOptions[+ver]) {
        const measureOption = _.find(this.measuresOptions[+ver], { alias: one.reportKey });
        if (measureOption) {
          one.isSupply = +measureOption.isSupplyMeasure;
          one.isDemand = +measureOption.isDemandMeasure;
        }
      }
      return one;
    });

    return measureColumns;
  }

  buildColumnsDefaults(ver: number = 1) {
    this.columnsDefaults = [
      // Special columns
      {
        key: '$$selected',
        label: '',
        width: 64,
        filterType: 'SELECT',
        parseTooltipFunction: this.parseSelectedTooltip,
        disableHide: true,
        resizable: false,
      },
      {
        key: '$$active',
        label: 'ACTIVE',
        width: 96,
        filterType: 'SELECT',
        parseTooltipFunction: this.parseActiveTooltip,
      },
      { key: '$$actions', label: '', width: 96, resizable: false, parseFunction: this.parseActions, mandatory: true },
      { key: '$$link', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$caret', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$style', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$disabled', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$badSelection', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$reportDummy', label: '', width: 32, resizable: false },
      { key: '$$itemId', label: '', width: 0, resizable: false, mandatory: true },
      { key: '$$supplyx', label: '', width: 0, resizable: false },
      { key: '$$demandx', label: '', width: 0, resizable: false },
      // Report columns
      { key: 'supply_id', label: 'SUPPLY_ID', width: 120, filterType: 'INPUT' },
      { key: 'supply_name', label: 'SUPPLY_NAME', width: 350, filterType: 'INPUT' },
      {
        key: 'supply_method',
        label: 'SUPPLY_METHOD',
        width: 110,
        filterType: 'DROPDOWN',
        parseFunction: this.parseSupplyMethod,
        parseTooltipFunction: this.parseSupplyMethod,
      },
      {
        key: 'supply_type',
        label: 'SUPPLY_TYPE',
        width: ver === 2 ? 136 : 104,
        filterType: 'DROPDOWN',
        parseFunction: this.parseSupplyType,
        parseTooltipFunction: this.parseSupplyType,
      },
      { key: 'demand_id', label: 'DEMAND_ID', width: 130, filterType: 'INPUT' },
      { key: 'demand_name', label: 'DEMAND_NAME', width: 350, filterType: 'INPUT' },
      {
        key: 'demand_type',
        label: 'DEMAND_TYPE',
        width: ver === 2 ? 148 : 120,
        filterType: 'DROPDOWN',
        parseFunction: this.parseDemandType,
        parseTooltipFunction: this.parseDemandType,
      },
      { key: 'demand_url', label: 'DEMAND_URL', width: 320, filterType: 'INPUT' },
      { key: 'vendor', label: 'VENDOR', width: 216, filterType: 'INPUT' },
      {
        key: 'sub_id',
        label: 'SUB_ID',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'sub_id2',
        label: 'SUB_ID2',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'sub_id3',
        label: 'SUB_ID3',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'sub_id4',
        label: 'SUB_ID4',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'utm_campaign',
        label: 'UTM_CAMPAIGN',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'utm_medium',
        label: 'UTM_MEDIUM',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'utm_source',
        label: 'UTM_SOURCE',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'utm_term',
        label: 'UTM_TERM',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'utm_content',
        label: 'UTM_CONTENT',
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'app_bundle',
        label: 'APP_BUNDLE',
        width: 300,
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'app_name',
        label: 'APP_NAME',
        width: 330,
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      { key: 'country', label: 'COUNTRY', width: 120, filterType: 'DROPDOWN' },
      {
        key: 'day',
        label: 'DAY',
        width: 94,
        parseFunction: this.parseDay,
        parseTooltipFunction: this.parseDay,
        filterType: 'SELECT',
      },
      {
        key: 'hour',
        label: 'HOUR',
        width: 83,
        parseFunction: this.parseHour,
        parseTooltipFunction: this.parseHour,
        filterType: 'SELECT',
      },
      {
        key: 'domain',
        label: 'DOMAIN',
        width: 250,
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'detected_domain',
        label: 'DETECTED_DOMAIN',
        width: 200,
        filterType: 'INPUT',
        parseFunction: this.parseSource,
        parseTooltipFunction: this.parseSourceTooltip,
      },
      {
        key: 'environment',
        label: 'ENVIRONMENT_SHORT',
        width: 96,
        filterType: 'DROPDOWN',
        parseFunction: this.parseEnvironment,
        parseTooltipFunction: this.parseEnvironmentTooltip,
      },
      { key: 'player_size', label: 'PLAYER_SIZE', width: 96, filterType: 'INPUT' },
      { key: 'detected_player_size', label: 'PLAYER_SIZE_DETECTED', width: 96, filterType: 'INPUT' },
      { key: 'player_version', label: 'PLAYER_VERSION', width: 110, filterType: 'DROPDOWN' },
      { key: 'ua_id', label: 'USER_AGENT', width: 150, filterType: 'INPUT' },
      { key: 'network_id', label: 'NETWORK_ID', filterType: 'INPUT' },
      { key: 'acc_manager', label: 'ACCOUNT_MANAGER', filterType: 'INPUT' },
      { key: 'network_type', label: 'NETWORK_TYPE', filterType: 'DROPDOWN' },
      { key: 'network_name', label: 'NETWORK_NAME', filterType: 'INPUT' },
      {
        key: 'supply_partner',
        label: 'SUPPLY_PARTNER',
        width: 120,
        filterType: 'DROPDOWN',
        parseFunction: this.parsePartner,
        parseTooltipFunction: this.parsePartner,
      },
      {
        key: 'demand_partner',
        label: 'DEMAND_PARTNER',
        width: 120,
        filterType: 'DROPDOWN',
        parseFunction: this.parsePartner,
        parseTooltipFunction: this.parsePartner,
      },
      {
        key: 'adStarts',
        label: 'IMPRESSIONS',
        parseFunction: this.parseNumeric,
        parseTooltipFunction: this.parseNumeric,
        rightAligned: true,
      },
      {
        key: 'adStartsPercent',
        label: 'AD_STARTS_PERCENTAGE',
        parseFunction: this.parseNumericPadd,
        parseTooltipFunction: this.parseNumericPadd,
        rightAligned: true,
        measurement: { char: '%', align: 'right' },
      },
      {
        key: 'loaded',
        label: 'PLAYER_LOADS',
        parseFunction: this.parseNumeric,
        parseTooltipFunction: this.parseNumeric,
        rightAligned: true,
      },
      {
        key: 'adStartRPM',
        label: 'RPM',
        parseFunction: this.parseNumericPadd,
        parseTooltipFunction: this.parseNumericPadd,
        rightAligned: true,
        measurement: { char: '$', align: 'left' },
      },
      {
        key: 'loadedPercent',
        label: 'PLAYER_LOADS_PERCENTAGE',
        parseFunction: this.parseNumericPadd,
        parseTooltipFunction: this.parseNumericPadd,
        rightAligned: true,
        measurement: { char: '%', align: 'right' },
      },
      // Other columns
      {
        key: 'active',
        label: 'ACTIVE',
        filterType: 'SELECT',
        width: 120,
        parseFunction: this.parseActive,
        parseTooltipFunction: this.parseActiveTooltip,
      },
      {
        key: 'CPM',
        label: 'CPM',
        measureFilter: true,
        char: '$',
        width: 96,
        parseFunction: this.parseNumericPadd,
        parseTooltipFunction: this.parseNumericPadd,
        rightAligned: true,
      },
      {
        key: 'list',
        label: 'LIST',
        parseFunction: this.parseList,
        filterType: 'SELECT',
        parseTooltipFunction: this.parseListTooltip,
      },
      {
        key: 'request_limit',
        label: 'REQUEST_LIMIT',
        parseFunction: this.parseBoolean,
        parseTooltipFunction: this.parseBooleanTooltip,
      },
      {
        key: 'premium',
        label: 'PREMIUM',
        parseFunction: this.parseBoolean,
        parseTooltipFunction: this.parseBooleanTooltip,
      },
      { key: 'url', label: 'URL', width: 200, filterType: 'INPUT' },
      { key: 'country_code', label: 'COUNTRY_CODE', filterType: 'INPUT' },
      { key: 'player_size', label: 'PLAYER_SIZE', filterType: 'INPUT' },
      {
        key: 'player_type',
        label: 'PLAYER_TYPE',
        width: 160,
        filterType: 'DROPDOWN',
        parseFunction: this.parseSupplyType,
        parseTooltipFunction: this.parseSupplyType,
      },
      {
        key: 'vast_type',
        label: 'DEMAND_TYPE',
        width: 160,
        filterType: 'DROPDOWN',
        parseFunction: this.parseDemandType,
        parseTooltipFunction: this.parseDemandType,
      },
      {
        key: 'created',
        label: 'CREATED',
        filterType: 'INPUT',
        parseFunction: this.parseDayTime,
        parseTooltipFunction: this.parseDayTime,
      },
      {
        key: 'updated',
        label: 'UPDATED',
        filterType: 'INPUT',
        parseFunction: this.parseDayTime,
        parseTooltipFunction: this.parseDayTime,
      },
      {
        key: 'fileNype',
        label: 'FILE_TYPE',
        filterType: 'SELECT',
        parseFunction: this.parseFileType,
        parseTooltipFunction: this.parseFileType,
      },
      { key: 'vast_cpm_on_player', label: 'CPM', measureFilter: true, char: '$', rightAligned: true },
      { key: 'fixed_cost', label: 'FIXED_COST', measureFilter: true, char: '$', rightAligned: true },
      { key: 'revshare_cost', label: 'REVSHARE_COST', measureFilter: true, char: '%', rightAligned: true },
      {
        key: 'capping_pause',
        label: 'REACHED_CAPPING_SETTINGS',
        filterType: 'DROPDOWN',
        parseFunction: this.parseBoolean,
        parseTooltipFunction: this.parseBooleanTooltip,
        width: 100,
      },
      { key: '$$defaultCPM', label: 'DEFAULT_CPM', width: 192 },
      { key: '$$CPM', label: 'CPM', measureFilter: true, char: '$', rightAligned: true },
      {
        key: '$$passedPayment',
        label: 'PAYMENT_DUE',
        filterType: 'SELECT',
        parseTooltipFunction: this.parsePassedPaymentTooltip,
      },
    ];

    switch (ver) {
      case 2:
        this.columnsDefaults = _.union(this.columnsDefaults, [
          { key: 'content_id', label: 'CONTENT_ID', measureFilter: true, width: 110, filterType: 'INPUT' },
          { key: 'content_name', label: 'CONTENT_NAME', measureFilter: true, width: 160, filterType: 'INPUT' },
          { key: 'dma', label: 'DMA', width: 160, filterType: 'DROPDOWN' },
          { key: 'state', label: 'STATE', width: 160, filterType: 'DROPDOWN' },
          {
            key: 'cost',
            label: 'COST',
            measureFilter: true,
            char: '$',
            width: 74,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'demand_cost',
            label: 'DEMAND_COST',
            measureFilter: true,
            char: '$',
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            visible: false,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'ad_impression',
            label: 'IMPRESSIONS',
            measureFilter: true,
            width: 124,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'revenue',
            label: 'REVENUE',
            measureFilter: true,
            char: '$',
            width: 110,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'supply_opportunities',
            label: 'OPPORTUNITIES',
            measureFilter: true,
            width: 135,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'demand_opportunities',
            label: 'OPPORTUNITIES',
            measureFilter: true,
            width: 135,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'supply_loaded',
            label: 'SUPPLY_LOADED',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'ad_q1_played',
            label: 'AD_Q1_PLAYED',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_q2_played',
            label: 'AD_Q2_PLAYED',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_q3_played',
            label: 'AD_Q3_PLAYED',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_q4_played',
            label: 'AD_Q4_PLAYED',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'vcr',
            label: 'VCR',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'ad_clicked',
            label: 'AD_CLICKED',
            measureFilter: true,
            width: 84,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'content_clicks',
            label: 'CONTENT_CLICKS',
            measureFilter: true,
            width: 138,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'content_viewed',
            label: 'CONTENT_VIEWED',
            measureFilter: true,
            width: 138,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'content_completed',
            label: 'CONTENT_COMPLETED',
            measureFilter: true,
            width: 138,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'ad_loaded',
            label: 'AD_LOADED',
            measureFilter: true,
            width: 100,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_start_avg_time',
            label: 'AD_START_AVG_TIME',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            decimal: true,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_load_avg_time',
            label: 'AD_LOAD_AVG_TIME',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            decimal: true,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'ad_requests',
            label: 'AD_REQUESTS',
            measureFilter: true,
            width: 126,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'start_requests',
            label: 'START_REQUESTS',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'demand_fr',
            label: 'DEMAND_FR',
            measureFilter: true,
            char: '%',
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'fillrate',
            label: 'FILLRATE',
            measureFilter: true,
            char: '%',
            width: 100,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'rpm',
            label: 'RPM',
            measureFilter: true,
            char: '$',
            width: 74,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'impression_rpm',
            label: 'IMPRESSION_RPM',
            measureFilter: true,
            char: '$',
            width: 160,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'previewed',
            label: 'PREVIEWED',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'previews',
            label: 'PREVIEWS',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'viewability',
            label: 'VIEWABILITY',
            measureFilter: true,
            char: '%',
            width: 160,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'viewability_sampled',
            label: 'VIEWABILITY_SAMPLED',
            measureFilter: true,
            char: '%',
            width: 160,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'rtb_bid_requests',
            label: 'BID_REQUESTS',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_bid_responses',
            label: 'BID_RESPONSES',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_no_bid_responses',
            label: 'NO_BID_RESPONSES',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_bid_cpm',
            label: 'BID_CPM',
            measureFilter: true,
            width: 160,
            char: '$',
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'prebid_requests',
            label: 'BID_REQUESTS',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'prebid_responses',
            label: 'BID_RESPONSES',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'no_bid_responses',
            label: 'NO_BID_RESPONSES',
            measureFilter: true,
            width: 160,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            defaultVisibility: false,
          },
          {
            key: 'prebid_cpm',
            label: 'BID_CPM',
            measureFilter: true,
            width: 160,
            char: '$',
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            defaultVisibility: false,
            measurement: { char: '$', align: 'left' },
          },
        ]);
        break;

      default:
        this.columnsDefaults = _.union(this.columnsDefaults, [
          {
            key: 'cost',
            label: 'COST',
            measureFilter: true,
            char: '$',
            width: 100,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'ad_requests',
            label: 'AD_REQUESTS',
            measureFilter: true,
            width: 130,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'impressions',
            label: 'IMPRESSIONS',
            measureFilter: true,
            width: 120,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'revenue',
            label: 'REVENUE',
            measureFilter: true,
            char: '$',
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'impression_rpm',
            label: 'IMPRESSION_RPM',
            measureFilter: true,
            char: '$',
            width: 160,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'opportunities',
            label: 'OPPORTUNITIES',
            measureFilter: true,
            width: 130,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
          },
          {
            key: 'rpm',
            label: 'RPM',
            measureFilter: true,
            char: '$',
            width: 100,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'RPM',
            label: 'RPM',
            measureFilter: true,
            char: '$',
            width: 100,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '$', align: 'left' },
          },
          {
            key: 'fillrate',
            label: 'FILLRATE',
            measureFilter: true,
            char: '%',
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'demand_fr',
            label: 'DEMAND_FR',
            measureFilter: true,
            char: '%',
            width: 120,
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            measurement: { char: '%', align: 'right' },
          },
          {
            key: 'rtb_bid_requests',
            label: 'RTB_BID_REQUESTS',
            measureFilter: true,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_bid_responses',
            label: 'RTB_BID_RESPONSES',
            measureFilter: true,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_no_bid_responses',
            label: 'RTB_NO_BID_RESPONSES',
            measureFilter: true,
            parseFunction: this.parseNumeric,
            parseTooltipFunction: this.parseNumeric,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
          },
          {
            key: 'rtb_bid_cpm',
            label: 'RTB_BID_CPM',
            measureFilter: true,
            char: '$',
            parseFunction: this.parseNumericPadd,
            parseTooltipFunction: this.parseNumericPadd,
            decimal: true,
            rightAligned: true,
            isRTBColumn: true,
            defaultVisibility: false,
            measurement: { char: '$', align: 'left' },
          },
        ]);
    }

    _.forEach(this.columnsDefaults, (one) => {
      if (one.measureFilter && this.userService.getFeatureState('measures_filter')) {
        delete one.measureFilter;
        one.filterType = 'RANGE';

        if (one.char) {
          if (one.char === '$') {
            one.filterMeasurement = { char: '$', align: 'left' };
          } else if (one.char === '%') {
            one.filterMeasurement = { char: '%', align: 'right' };
          }
          delete one.char;
        }
      }
    });

    if (this.userService.isCedatoPublisher()) {
      _.forEach(this.columnsDefaults, (one) => {
        switch (one.label) {
          case 'SUPPLY_ID':
            one.label = 'INVENTORY_ID';
            break;

          case 'SUPPLY_NAME':
            one.label = 'INVENTORY_NAME';
            break;

          case 'COST':
            one.label = 'REVENUE';
            break;

          default:
            break;
        }
      });
    }
  }

  buildRTBColumns() {
    this.rtbColumns = [];
    for (let i = 0; i < this.columnsDefaults.length; i++) {
      if (this.columnsDefaults[i] && this.columnsDefaults[i].isRTBColumn) {
        this.rtbColumns.push(this.columnsDefaults[i].key);
      }
    }
  }
}
